import React, { useRef } from 'react';
import { expandFontTheme, theme, IconClose, useThemeContext } from '@contrast-security-inc/design-system-foundations';
import { TextOverflow } from '@contrast-security-inc/design-system-popovers';
import { css, cx } from '@emotion/css';
import { selectInputWrapperOpenMenuStyles, selectInputWrapperStyles, inputDisabledStyles, inputErrorStyles, inputWrapperStyles, labelAsPlaceholderStyles, selectedItemTextStyles, selectedItemsWrapperStyles, buttonResetStyles, } from '../shared';
import { ListboxActions } from './ListboxActions';
const customLabelAsPlaceholderStyles = (placeholder) => {
    if (!placeholder) {
        return css `
      input:placeholder-shown:focus + label,
      input:placeholder-shown:focus ~ fieldset > legend {
        transition-delay: 0s;
      }

      input:placeholder-shown:not(:focus) + label,
      input:placeholder-shown:not(:focus) ~ fieldset > legend {
        transition-delay: 0.2s;
      }
    `;
    }
};
const returnLabelAsPlaceholderStyles = (useLabelAsPlaceholderStyles, selectedTheme) => {
    if (useLabelAsPlaceholderStyles) {
        return labelAsPlaceholderStyles(selectedTheme);
    }
};
const returnInputErrorStyles = (error, selectedTheme) => {
    if (error) {
        return inputErrorStyles(selectedTheme);
    }
};
const returnInputDisabledStyles = (isDisabled, selectedTheme) => {
    if (isDisabled) {
        return inputDisabledStyles(selectedTheme);
    }
};
const selectedItemStyles = (showPlaceholderTag, isDisabled) => css `
  ${expandFontTheme(theme.font.metadata)};
  align-items: center;
  border-radius: 20px;
  border: 1px solid ${theme.color.neutral60};
  box-sizing: border-box;
  display: flex;
  height: 24px;
  max-width: 300px;
  overflow: hidden;
  padding: 4px ${showPlaceholderTag || isDisabled ? '12px' : '28px'} 4px 12px;
  position: relative;

  .inner-text {
    white-space: nowrap;
    overflow: hidden;
  }

  .remove-icon {
    display: flex;
    cursor: pointer;
    padding: 4px 0px;
    position: absolute;
    right: 6px;
    transform: scale(0.9);
    transition: 0.2s ease all;

    &:hover {
      transform: scale(1.25);
    }
  }
`;
const getRegularMultiSelectVariables = (data) => {
    const { placeholder, placeholderTag, selectedItems } = data;
    const showInputValueSpan = false;
    let placeholderValue;
    let useLabelAsPlaceholderStyles;
    /**
     * if no items are selected and the `placeholderTag` prop is set,
     * don't allow the use of `labelAsPlaceholderStyles` and force the
     * placeholder to be an empty string
     */
    if (selectedItems.length === 0 && placeholderTag) {
        placeholderValue = ' ';
        useLabelAsPlaceholderStyles = false;
    }
    else {
        placeholderValue = (selectedItems.length === 0 && placeholder) || ' ';
        useLabelAsPlaceholderStyles = !placeholder && selectedItems.length === 0;
    }
    return { placeholderValue, showInputValueSpan, useLabelAsPlaceholderStyles };
};
const getCheckboxMultiSelectVariables = (data) => {
    const { inputValue, placeholder, placeholderTag, selectedItems } = data;
    const showInputValueSpan = !inputValue && selectedItems.length;
    const placeholderAndNoSelectedItems = placeholder && !selectedItems.length;
    let placeholderValue = placeholderAndNoSelectedItems ? placeholder : ' ';
    let useLabelAsPlaceholderStyles;
    /**
     * Similar to isRegularMultiSelect, if no items are selected and the
     * `placeholderTag` prop is set then don't allow the use of
     * `labelAsPlaceholderStyles` and force the placeholder value
     * to be the `placeholderTag` prop value
     */
    if (selectedItems.length === 0 && placeholderTag) {
        placeholderValue = placeholderTag;
        useLabelAsPlaceholderStyles = false;
    }
    else {
        useLabelAsPlaceholderStyles = !placeholder && selectedItems.length === 0;
    }
    return { placeholderValue, showInputValueSpan, useLabelAsPlaceholderStyles };
};
const getPlaceholderRelatedVariables = (data) => {
    const { inputValue, isRegularMultiSelect, isCheckboxMultiSelect, isOpen, placeholder, selectedItem } = data;
    let placeholderValue;
    let showInputValueSpan;
    let useLabelAsPlaceholderStyles = !placeholder;
    if (isRegularMultiSelect) {
        const { placeholderValue: placeholderValueRegular, showInputValueSpan: showInputValueSpanRegular, useLabelAsPlaceholderStyles: useLabelAsPlaceholderStylesRegular, } = getRegularMultiSelectVariables(data);
        placeholderValue = placeholderValueRegular;
        showInputValueSpan = showInputValueSpanRegular;
        useLabelAsPlaceholderStyles = useLabelAsPlaceholderStylesRegular;
    }
    else if (isCheckboxMultiSelect) {
        const { placeholderValue: placeholderValueCheckbox, showInputValueSpan: showInputValueSpanCheckbox, useLabelAsPlaceholderStyles: useLabelAsPlaceholderStylesCheckbox, } = getCheckboxMultiSelectVariables(data);
        placeholderValue = placeholderValueCheckbox;
        showInputValueSpan = showInputValueSpanCheckbox;
        useLabelAsPlaceholderStyles = useLabelAsPlaceholderStylesCheckbox;
    }
    else {
        const placeholderAndNoSelectedItem = placeholder && !selectedItem;
        placeholderValue = placeholderAndNoSelectedItem ? placeholder : ' ';
        showInputValueSpan = isOpen && !inputValue && selectedItem;
    }
    return { placeholderValue, showInputValueSpan, useLabelAsPlaceholderStyles };
};
export const SelectedItemTag = ({ isDisabled, getSelectedItemProps, index, item, itemToString, placeholderTag, removeSelectedItem, testId, ...props }) => {
    const { selectedTheme } = useThemeContext();
    const textItemElement = useRef(null);
    const tagText = !item && placeholderTag ? placeholderTag : itemToString(item);
    const showRemoveIcon = item && !isDisabled;
    const dynamicGetSelectedItemProps = !isDisabled ? { ...getSelectedItemProps({ selectedItem: item, index }) } : {};
    return (React.createElement("span", { className: selectedItemStyles(!item && placeholderTag, isDisabled), "data-testid": `${testId}-selected-item-tag-${index}`, ...dynamicGetSelectedItemProps, ...props },
        React.createElement(TextOverflow, { className: "inner-text", ref: textItemElement, font: "metadata", offset: [0, 6] }, tagText),
        showRemoveIcon && (React.createElement("span", { className: "remove-icon", "data-testid": `remove-tag-${itemToString(item)}`, onClick: () => {
                removeSelectedItem(item);
            } },
            React.createElement(IconClose, { size: "sm", color: selectedTheme === 'dark' ? 'white' : 'black' })))));
};
export const ComboboxInput = ({ ariaLabel, className, clearSelection, dropdownWrapperRef, describers, error, getInputProps = (_p) => ({}), getLabelProps = (_p) => ({}), getSelectedItemProps = (_p) => ({}), getToggleButtonProps = (_p) => ({}), inputHeight, inputValue = '', isDisabled, isOpen, isRequired, itemToString, label, onBlur, openMenu = () => undefined, placeholder = '', selectedItem, selectInput, testId, uniqueId = '', multiSelectProps = {}, }) => {
    const { selectedTheme } = useThemeContext();
    const { isMultiSelect = false, isRegularMultiSelect = false, isCheckboxMultiSelect = false, getDropdownProps = (_p) => ({}), selectedItems = [], removeSelectedItem = () => undefined, placeholderTag, showClearAllIcon, } = multiSelectProps;
    /**
     * Based on being multi-select or not,
     * fetch the appropriately defined variables
     */
    const { placeholderValue, showInputValueSpan, useLabelAsPlaceholderStyles } = getPlaceholderRelatedVariables({
        inputValue,
        isRegularMultiSelect,
        isCheckboxMultiSelect,
        isOpen,
        placeholder,
        placeholderTag,
        selectedItem,
        selectedItems,
    });
    const inputProps = {
        ref: selectInput,
        'aria-describedby': describers,
        'aria-errormessage': `${uniqueId}-error`,
        'aria-invalid': !!error,
        'aria-label': placeholder,
        disabled: isDisabled,
        id: uniqueId,
        placeholder: placeholderValue,
        required: isRequired,
        type: 'text',
    };
    const labelProps = getLabelProps({ htmlFor: uniqueId });
    const isLabelVisible = !label || !!placeholder;
    return (React.createElement("div", { "data-testid": `${testId}-input-wrapper`, className: cx(inputWrapperStyles(label), selectInputWrapperStyles(isRegularMultiSelect, selectedTheme), returnLabelAsPlaceholderStyles(useLabelAsPlaceholderStyles, selectedTheme), customLabelAsPlaceholderStyles(placeholder), returnInputDisabledStyles(isDisabled, selectedTheme), returnInputErrorStyles(error, selectedTheme), selectInputWrapperOpenMenuStyles(isOpen), className), ref: dropdownWrapperRef },
        React.createElement("span", { className: selectedItemsWrapperStyles(isRegularMultiSelect, inputHeight, true, isDisabled), onClick: (event) => {
                const isNotDisabledIsNotOpen = !isDisabled && !isOpen;
                if (isNotDisabledIsNotOpen && event.target === event.currentTarget) {
                    openMenu();
                }
            }, onBlur: (event) => {
                if (onBlur) {
                    onBlur(event);
                }
            }, "data-testid": `${testId}-input-wrapper-span` },
            isRegularMultiSelect && selectedItems.length > 0 ? (selectedItems.map((item, index) => (React.createElement(SelectedItemTag, { isDisabled: isDisabled, getSelectedItemProps: getSelectedItemProps, index: index, item: item, itemToString: itemToString, key: `selected-item-${item ? itemToString(item) : index}`, removeSelectedItem: removeSelectedItem, testId: testId })))) : (React.createElement(React.Fragment, null, isRegularMultiSelect && placeholderTag && (React.createElement(SelectedItemTag, { isDisabled: isDisabled, getSelectedItemProps: getSelectedItemProps, index: 0, item: null, itemToString: itemToString, removeSelectedItem: removeSelectedItem, placeholderTag: placeholderTag, testId: testId })))),
            React.createElement("input", { ...getInputProps(isMultiSelect ? getDropdownProps(inputProps) : inputProps), className: css `
            text-overflow: ellipsis;
            ${isCheckboxMultiSelect && 'width: 100%;'}
          `, "data-testid": `${testId}-input` }),
            showInputValueSpan && isCheckboxMultiSelect ? (React.createElement("button", { 
                // Define button reset styles first, let other styles override reset styles as needed
                className: cx(buttonResetStyles, selectedItemTextStyles(true, selectedTheme)), "data-testid": `${testId}-selected-item-input-value`, type: "button", onClick: () => {
                    if (!isDisabled && !isOpen) {
                        openMenu();
                    }
                } },
                React.createElement("span", { className: "selected-item-text-textoverflow-wrapper" },
                    React.createElement(TextOverflow, { font: "bodyStrong", offset: [0, 8], testId: `${testId}-selected-item-input-value-tooltip` }, placeholderTag && !selectedItems.length
                        ? placeholderTag
                        : selectedItems?.map((item) => item && itemToString(item)).join(', '))))) : null,
            showInputValueSpan && !isCheckboxMultiSelect ? (React.createElement("span", { className: selectedItemTextStyles(true, selectedTheme), "data-testid": `${testId}-selected-item-input-value` },
                React.createElement("span", { className: "selected-item-text-textoverflow-wrapper" },
                    React.createElement(TextOverflow, { font: "bodyStrong", offset: [0, 8], testId: `${testId}-selected-item-input-value-tooltip` }, selectedItem && itemToString(selectedItem))))) : null,
            React.createElement("label", { ...labelProps, hidden: isLabelVisible, className: cx(labelProps.className, css `
                &&& {
                  display: ${isLabelVisible ? 'none' : undefined};
                }
              `), "data-testid": `${testId}-label` }, label || ariaLabel),
            React.createElement("fieldset", null, label && React.createElement("legend", null, label))),
        React.createElement(ListboxActions, { clearSelection: clearSelection, getToggleButtonProps: getToggleButtonProps, isDisabled: isDisabled, isMultiSelect: isMultiSelect, isOpen: isOpen, selectedItem: selectedItem, selectedItems: selectedItems, showClearAllIcon: showClearAllIcon, testId: `${testId}-actions` })));
};
